import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { ChartBar, ChartDonut, Package } from "@phosphor-icons/react";
import TiersTable from "./TiersTable";
import StockAgingCard from "./StockAgingCard";
import { TIERS, CAT_COLORS, convert, money, compact } from "./tiersShared";

function Segmented({ value, onChange, options, testid }) {
  return (
    <div className="inline-flex border border-zinc-200 rounded-sm overflow-hidden bg-white text-xs" data-testid={testid}>
      {options.map((o) => (
        <button key={o.value} type="button" onClick={() => onChange(o.value)}
          className={`px-3 py-1.5 font-semibold transition-colors ${value === o.value ? "bg-[#002FA7] text-white" : "text-zinc-600 hover:bg-zinc-50"}`}
          data-testid={`${testid}-${o.value}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function BarTooltip({ active, payload, label, cur, mode }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-zinc-200 shadow-md p-3 text-xs min-w-[190px]">
      <div className="font-semibold text-zinc-900 mb-1.5">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-4 py-0.5">
          <span className="inline-flex items-center gap-1.5 text-zinc-600"><span className="w-2 h-2 rounded-sm" style={{ background: p.color }} />{p.name}</span>
          <span className="numeric font-semibold">{mode === "value" ? money(p.value, cur) : p.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function InventoryTiersSection() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [cur, setCur] = useState("MXN");
  const [mode, setMode] = useState("value");
  const [hidden, setHidden] = useState({});

  useEffect(() => {
    api.get("/reports/inventory-tiers").then((r) => setData(r.data)).catch(() => setError(true));
  }, []);

  const rate = data?.exchange_rate || 1;
  const cats = data?.categories || [];
  const colorOf = (i) => CAT_COLORS[i % CAT_COLORS.length];

  const barData = useMemo(() => cats.map((c) => {
    const row = { name: c.name, items: c.items, products: c.products };
    TIERS.forEach((t) => { row[t.key] = Math.round(convert(c[`${t.key}_usd`], cur, rate)); });
    return row;
  }), [cats, cur, rate]);

  const donut = useMemo(() => {
    const total = cats.reduce((a, c) => a + Number(c.store_usd || 0), 0);
    return {
      total,
      slices: cats.map((c, i) => ({ name: c.name, value: Number(c.store_usd || 0), pct: total > 0 ? (c.store_usd / total) * 100 : 0, color: colorOf(i) })),
    };
    // eslint-disable-next-line
  }, [cats]);

  const toggleTier = (key) => setHidden((h) => ({ ...h, [key]: !h[key] }));

  if (error) return null;
  if (!data) return <div className="text-sm text-zinc-500">Cargando inventario disponible…</div>;

  return (
    <section className="space-y-4" data-testid="inventory-tiers-section">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="font-mono-data text-[10px] tracking-widest text-zinc-500">INVENTARIO DISPONIBLE · STOCK &gt; 0</div>
          <h2 className="font-heading text-xl font-bold mt-0.5">Valor por categoría y tier de precio</h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            {data.totals.items} piezas · {data.totals.products} productos · {data.totals.categories} categorías · TC <span className="numeric">1 USD = {rate} MXN</span>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Segmented value={cur} onChange={setCur} testid="tiers-currency" options={[{ value: "MXN", label: "MX$" }, { value: "USD", label: "USD" }]} />
          <Segmented value={mode} onChange={setMode} testid="tiers-mode" options={[{ value: "value", label: "Valor" }, { value: "units", label: "Unidades" }]} />
        </div>
      </div>

      {cats.length === 0 ? (
        <div className="border border-dashed border-zinc-300 p-10 text-center text-sm text-zinc-400 bg-white" data-testid="tiers-empty">
          <Package size={28} className="mx-auto mb-2 text-zinc-300" /> No hay productos con stock disponible.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
            {/* Grouped bars */}
            <div className="xl:col-span-8 border border-zinc-200 bg-white p-4" data-testid="tiers-bar-card">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <div className="text-xs font-semibold text-zinc-700 inline-flex items-center gap-1.5">
                  <ChartBar size={14} weight="bold" className="text-[#002FA7]" />
                  {mode === "value" ? `Valor del stock por tier (${cur === "MXN" ? "MX$" : "USD"})` : "Piezas y productos por categoría"}
                </div>
                {mode === "value" && (
                  <div className="flex flex-wrap gap-1.5" data-testid="tiers-legend">
                    {TIERS.map((t) => {
                      const off = !!hidden[t.key];
                      return (
                        <button key={t.key} type="button" onClick={() => toggleTier(t.key)}
                          className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border text-[11px] font-medium transition-colors ${off ? "bg-white text-zinc-400 border-zinc-200 line-through" : "bg-zinc-50 text-zinc-800 border-zinc-200 hover:border-zinc-400"}`}
                          title={off ? `Mostrar ${t.label}` : `Ocultar ${t.label}`}
                          data-testid={`tiers-legend-${t.key}`}>
                          <span className="w-2.5 h-2.5 rounded-sm" style={{ background: off ? "#d4d4d8" : t.color }} /> {t.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="h-[320px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }} barGap={2} barCategoryGap="22%">
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e4e4e7" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#52525b" }} axisLine={false} tickLine={false} interval={0} />
                    <YAxis tick={{ fontSize: 11, fill: "#71717a" }} axisLine={false} tickLine={false} width={64}
                      tickFormatter={(v) => (mode === "value" ? compact(v, cur) : v)} />
                    <Tooltip cursor={{ fill: "#f4f4f5" }} content={<BarTooltip cur={cur} mode={mode} />} />
                    {mode === "value"
                      ? TIERS.map((t) => <Bar key={t.key} dataKey={t.key} name={t.label} fill={t.color} hide={!!hidden[t.key]} radius={[3, 3, 0, 0]} maxBarSize={38} />)
                      : (
                        <>
                          <Bar dataKey="items" name="Piezas" fill="#002FA7" radius={[3, 3, 0, 0]} maxBarSize={48} />
                          <Bar dataKey="products" name="Productos" fill="#FFC000" radius={[3, 3, 0, 0]} maxBarSize={48} />
                        </>
                      )}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Donut */}
            <div className="xl:col-span-4 border border-zinc-200 bg-white p-4 flex flex-col" data-testid="tiers-donut-card">
              <div className="text-xs font-semibold text-zinc-700 inline-flex items-center gap-1.5 mb-1">
                <ChartDonut size={14} weight="bold" className="text-[#002FA7]" /> Participación por categoría
              </div>
              <div className="text-[11px] text-zinc-500 mb-2">A precio Tienda</div>
              <div className="relative h-[210px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={donut.slices} dataKey="value" nameKey="name" innerRadius={62} outerRadius={92} paddingAngle={2} stroke="#fff" strokeWidth={2}>
                      {donut.slices.map((s) => <Cell key={s.name} fill={s.color} />)}
                    </Pie>
                    <Tooltip formatter={(v, n) => [money(convert(v, cur, rate), cur), n]} contentStyle={{ fontSize: 12, border: "1px solid #e4e4e7" }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total tienda</div>
                  <div className="numeric font-bold text-lg text-[#002FA7]" data-testid="tiers-donut-total">{compact(convert(donut.total, cur, rate), cur)}</div>
                </div>
              </div>
              <ul className="mt-2 space-y-1.5 text-xs" data-testid="tiers-donut-legend">
                {donut.slices.map((s) => (
                  <li key={s.name} className="flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-2 text-zinc-700 truncate"><span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: s.color }} />{s.name}</span>
                    <span className="numeric text-zinc-900 whitespace-nowrap"><b>{s.pct.toFixed(1)}%</b> <span className="text-zinc-500">· {compact(convert(s.value, cur, rate), cur)}</span></span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <StockAgingCard
            categories={cats}
            agingTotals={data.aging_totals || {}}
            buckets={data.age_buckets || []}
            stagnant={data.stagnant || []}
            cur={cur}
            rate={rate}
          />

          <TiersTable categories={cats} totals={data.totals} cur={cur} rate={rate} colorOf={colorOf} />
          <p className="text-[11px] text-zinc-400">Haz clic en una categoría de la tabla para ver el desglose por marca. Margen potencial = valor a precio Tienda − valor a Costo.</p>
        </>
      )}
    </section>
  );
}
